import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import DropdownMenu from '../dropdown-menu/DropdownMenu';
import MediaDetailModal from '../media-preview/components/MediaDetailModal';
import MediaTranscriptionModal from '../media-preview/components/MediaTranscriptionModal';
import { MediaPreviewHandle } from '../../types/media-preview';
import { AcrScanStatus, AcrScanResult } from '../../types/acr';
import { getAcrIconMeta } from '../../utils/acr';
import { buildAudioMenuItems } from '../../utils/audioMenu';
import { downloadMediaFile } from '../../utils/downloadFile';

import WaveformCanvas from './WaveformCanvas';
import { usePeaks } from './core/usePeaks';
import { useAudioTransport } from './core/useAudioTransport';
import { useVisibleOnce } from './core/useVisibleOnce';
import { estimatePeaksDuration, formatAudioTime } from './core/peaks';

/**
 * コミュニケーションプレビュー（Messenger 内オーディオ添付）。
 *
 * 正確さよりパフォーマンスと見た目を優先した軽量プレイヤー。
 * - 1 行目: 再生/停止アイコン + モノ波形（事前計算 peaks の静的描画・即時表示）+ 現在秒数
 * - 2 行目: ファイル名 + 右下に ACR アイコン・三点リーダー（動画・画像プレビューと同配置）
 * - 波形 JSON が無いファイル（送信直後・Lambda 未生成のローカル環境等）は、
 *   画面内に入ってからブラウザ側 decode にフォールバックする（一覧での一斉デコード防止）。
 *   decode も失敗した場合のみシークバー表示に降格する
 */

export interface CommunicationAudioPlayerProps {
  src: string;
  /** Lambda 事前計算の peaks JSON URL（無ければ可視化後に decode フォールバック） */
  waveformUrl?: string | null;
  /** peaks キャッシュキー（ファイル ID 等）。未指定時は waveformUrl / src を使用 */
  cacheKey?: string;
  fileName?: string;
  downloadUrl?: string;
  onDownload?: () => void;
  /** メニュー「プレビュー」（モーダルで波形プレビューを開く） */
  onOpenPreview?: () => void;
  showMenu?: boolean;
  onPlayStateChange?: (_playing: boolean) => void;
  // ACR 楽曲認識
  fileId?: string;
  acrScanStatus?: AcrScanStatus;
  acrScanResult?: AcrScanResult | null;
  acrScannedAt?: string | null;
  className?: string;
  style?: React.CSSProperties;
}

const WAVEFORM_HEIGHT = 28;

const Container = styled.div`
  width: 100%;
  padding: 8px 12px;
  background-color: var(--soremo-background);
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const PlayButton = styled.span<{ $isPlaying?: boolean }>`
  font-size: 28px;
  color: ${props => props.$isPlaying ? 'var(--soremo-blue)' : 'var(--soremo-light-gray)'};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease-in-out;
  user-select: none;
  line-height: 1;

  &:hover {
    color: var(--soremo-blue);
    scale: 1.1;
  }
`;

const WaveArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const TimeText = styled.span.attrs({
  className: 'bodytext-11',
})`
  flex-shrink: 0;
  color: var(--soremo-light-gray);
  line-height: 1;
  user-select: none;
  font-variant-numeric: tabular-nums;
`;

/** 2 行目: ファイル名 + 右下の ACR / 三点リーダー（VideoPreview の menuOutside と同配置） */
const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const FooterFileName = styled.span.attrs({
  className: 'bodytext-11 u-line-height-150',
})`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--soremo-light-gray);
`;

const AcrBadge = styled.span`
  font-size: 18px;
  flex-shrink: 0;
  line-height: 1;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

// --- 波形 JSON なしの降格表示（シークバー） ---

const SeekBarArea = styled.div`
  position: relative;
  width: 100%;
  height: ${WAVEFORM_HEIGHT}px;
  display: flex;
  align-items: center;
  cursor: pointer;
  touch-action: none;
`;

const SeekBarTrack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 1.5px;
  background: var(--soremo-border);
  overflow: hidden;
`;

const SeekBarFill = styled.div`
  height: 100%;
  background: var(--soremo-blue);
  border-radius: 1.5px;
`;

const SeekBarThumb = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--soremo-blue);
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const CommunicationAudioPlayer = forwardRef<MediaPreviewHandle, CommunicationAudioPlayerProps>(({
  src,
  waveformUrl,
  cacheKey,
  fileName,
  downloadUrl,
  onDownload,
  onOpenPreview,
  showMenu = true,
  onPlayStateChange,
  fileId,
  acrScanStatus,
  acrScanResult,
  acrScannedAt,
  className,
  style,
}, ref) => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 波形 JSON は即 fetch。無い / 失敗した場合は画面内に入ってから decode フォールバック
  const visible = useVisibleOnce(containerRef);
  const { peaks, error: peaksError } = usePeaks({
    cacheKey: cacheKey ?? fileId ?? waveformUrl ?? src,
    waveformUrl,
    fallbackSrc: visible ? src : undefined,
    enabled: !!waveformUrl || visible,
  });

  const transport = useAudioTransport({
    src,
    preload: 'metadata',
    initialDuration: estimatePeaksDuration(peaks),
    onPlayStateChange,
  });
  const { isPlaying, currentTime, duration } = transport;

  // 再生前は総尺、再生開始後は現在秒数を表示する
  const displayTime = isPlaying || currentTime > 0 ? currentTime : duration;

  // 取得中はプレースホルダーバーを表示し、decode まで失敗した場合のみシークバーに降格
  const showWaveform = !peaksError;

  const handleDetailClick = useCallback(() => {
    setShowDetailModal(true);
  }, []);

  const handleDownloadClick = useCallback(() => {
    if (!downloadMediaFile({ downloadUrl, src, fileName, fallbackName: 'audio' })) return;
    onDownload?.();
  }, [downloadUrl, src, fileName, onDownload]);

  const handleTranscribeClick = useCallback(() => {
    setShowTranscriptionModal(true);
  }, []);

  const menuItems = useMemo(() => buildAudioMenuItems({
    onPreview: onOpenPreview,
    onDetailInfo: handleDetailClick,
    onDownload: handleDownloadClick,
    onTranscribe: fileId ? handleTranscribeClick : undefined,
    acrScanStatus,
  }), [onOpenPreview, handleDetailClick, handleDownloadClick, handleTranscribeClick, fileId, acrScanStatus]);

  const acrIcon = getAcrIconMeta(acrScanStatus);

  // --- 外部 API 公開（MediaPreview ファサード経由の再生制御用） ---
  useImperativeHandle(ref, () => ({
    play: async () => transport.play(),
    pause: () => transport.pause(),
    seek: (seconds: number) => transport.seekBy(seconds),
    seekTo: (seconds: number) => transport.seekTo(seconds),
    getCurrentTime: () => transport.getAudioElement()?.currentTime ?? 0,
    getDuration: () => transport.getAudioElement()?.duration ?? 0,
    clickToggle: () => transport.toggle(),
    getMediaElement: () => transport.getAudioElement(),
  }), [transport]);

  // --- 降格シークバーのポインター操作 ---
  const seekBarRef = useRef<HTMLDivElement>(null);
  const seekFromClientX = useCallback((clientX: number) => {
    const bar = seekBarRef.current;
    if (!bar || duration <= 0) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    transport.seekTo(ratio * duration);
  }, [duration, transport]);

  const handleSeekPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    seekFromClientX(e.clientX);
  }, [seekFromClientX]);

  const handleSeekPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.buttons === 0) return;
    seekFromClientX(e.clientX);
  }, [seekFromClientX]);

  const handleSeekPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
  }, []);

  const progressPercent = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  return (
    <>
      <Container ref={containerRef} className={className} style={style}>
        <MainRow>
          <PlayButton
            className="material-symbols-rounded"
            onClick={transport.toggle}
            $isPlaying={isPlaying}
          >
            {isPlaying ? 'pause_circle' : 'play_circle'}
          </PlayButton>
          <WaveArea>
            {showWaveform ? (
              <WaveformCanvas
                peaks={peaks}
                currentTime={currentTime}
                duration={duration}
                height={WAVEFORM_HEIGHT}
                channelMode="mono"
                onSeek={transport.seekTo}
                showHoverIndicator
              />
            ) : (
              <SeekBarArea
                ref={seekBarRef}
                onPointerDown={handleSeekPointerDown}
                onPointerMove={handleSeekPointerMove}
                onPointerUp={handleSeekPointerUp}
              >
                <SeekBarTrack>
                  <SeekBarFill style={{ width: `${progressPercent}%` }} />
                </SeekBarTrack>
                <SeekBarThumb style={{ left: `${progressPercent}%` }} />
              </SeekBarArea>
            )}
          </WaveArea>
          <TimeText>{formatAudioTime(displayTime)}</TimeText>
        </MainRow>
        <FooterRow>
          {fileName && <FooterFileName>{fileName}</FooterFileName>}
          {acrIcon.show && (
            <AcrBadge
              className="material-symbols-rounded"
              style={{ color: acrIcon.color }}
              onClick={handleDetailClick}
            >
              error
            </AcrBadge>
          )}
          {showMenu && <DropdownMenu items={menuItems} />}
        </FooterRow>
      </Container>
      {showDetailModal && (
        <MediaDetailModal
          onClose={() => setShowDetailModal(false)}
          fileName={fileName}
          acrScanResult={acrScanResult}
          fileId={fileId}
          acrScanStatus={acrScanStatus}
          acrScannedAt={acrScannedAt}
        />
      )}
      {showTranscriptionModal && fileId && (
        <MediaTranscriptionModal
          fileId={fileId}
          fileName={fileName}
          onClose={() => setShowTranscriptionModal(false)}
          onSeek={seconds => transport.seekTo(seconds)}
        />
      )}
    </>
  );
});

CommunicationAudioPlayer.displayName = 'CommunicationAudioPlayer';

export default CommunicationAudioPlayer;
